import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from "react";
import FocusTrap from "focus-trap-react";
import { AnimatePresence, motion } from "framer-motion";
import { useAtom } from "jotai";
import {
  ArrowRightIcon,
  CaretRightIcon,
  CheckCircleIcon,
  CircleIcon,
  MagnifyingGlassIcon,
  UsersIcon,
  XIcon,
  type Icon,
} from "@phosphor-icons/react";
import { useI18N } from "common/hooks";

import { useScrollLock } from "@/hooks/useScrollLock";
import { cn } from "@/lib/utils";
import { useReplayHomeTour } from "@/features/home/tour/useHomeTour";
import {
  howToAnalytics,
  howToType,
  lessonAnalytics,
  lessonName,
  lessonStatus,
} from "@/analytics/learningHubAnalytics";
import {
  ALL_LEARNING_ITEMS,
  LEARNING_HUB_TABS,
  type LearningHubTabId,
  type LearningItem,
  type LearningItemState,
} from "./constants";
import LessonStepList from "./LessonStepList";
import { learningHubAtom } from "./state";
import { useLearningProgress, useLessonOpened } from "./useLearningProgress";

const FIND_PLACES_LESSON = { id: "find-places" };
import { useLessonSteps } from "./useLessonSteps";

/**
 * "How to use Sociability" (Figma `16013:208570`) — desktop's Learning Hub,
 * the modal twin of native's `LearningHubPage`.
 *
 * Two tabs over a shared header:
 * - **Find places** — one numbered walkthrough, straight from the CMS album
 *   of the same name, exactly as native's tab reads it.
 * - **Contribute** — the six lessons in two sections on the left; pressing one
 *   shows its steps on the right, where native pushes a lesson page.
 *
 * Mounted once by the home page and opened through `learningHubAtom`.
 */

const DIALOG_ID = "learning-hub";
const HEADING_ID = "learning-hub-heading";

/** Material's "emphasized decelerate", as the onboarding modals use. */
const EASE_OUT = [0.05, 0.7, 0.1, 1] as const;

const TAB_ICONS: Record<LearningHubTabId, Icon> = {
  "find-places": MagnifyingGlassIcon,
  contribute: UsersIcon,
};

const tabId = (id: LearningHubTabId) => `${DIALOG_ID}-tab-${id}`;
const panelId = (id: LearningHubTabId) => `${DIALOG_ID}-panel-${id}`;

const LearningHubModal = () => {
  const [hub, setHub] = useAtom(learningHubAtom);
  const close = () => setHub((current) => ({ ...current, open: false }));
  // The ✕, Escape and the backdrop. "Start the guided tour" closes the hub
  // too, but goes on to the tour rather than leaving, so it is not counted.
  const dismiss = () => {
    howToAnalytics.track("close_icon", {});
    close();
  };

  return (
    <AnimatePresence>
      {hub.open ? (
        <motion.div
          key="learning-hub"
          role="presentation"
          className="fixed inset-0 z-[1030] flex items-center justify-center bg-surface-modal p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.2 } }}
          exit={{ opacity: 0, transition: { duration: 0.2 } }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) dismiss();
          }}
        >
          <motion.div
            className="flex h-full max-h-[691px] w-full max-w-[1024px]"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              transition: { duration: 0.38, ease: EASE_OUT },
            }}
            exit={{ opacity: 0, y: 12, scale: 0.98, transition: { duration: 0.18 } }}
          >
            <LearningHubDialog
              initialTab={hub.tab}
              entryPoint={hub.entryPoint}
              onClose={close}
              onDismiss={dismiss}
            />
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

const LearningHubDialog = ({
  initialTab,
  entryPoint,
  onClose,
  onDismiss,
}: {
  initialTab: LearningHubTabId;
  entryPoint: string;
  onClose: () => void;
  onDismiss: () => void;
}) => {
  const i18n = useI18N();
  const replayHomeTour = useReplayHomeTour();
  const [activeTab, setActiveTab] = useState<LearningHubTabId>(initialTab);
  const tabRefs = useRef<Partial<Record<LearningHubTabId, HTMLButtonElement | null>>>({});
  useScrollLock(true);

  // Mounted once per opening.
  useEffect(() => {
    howToAnalytics.track("page_view_page", { entry_point: entryPoint });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeTab = (id: LearningHubTabId) => {
    if (id === activeTab) return;
    howToAnalytics.track("tab_tab", { how_to_type: howToType(id) });
    setActiveTab(id);
  };

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onDismiss]);

  // Arrow keys move between tabs, the WAI-ARIA tabs pattern with automatic
  // activation — each panel is cheap to show.
  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const ids = LEARNING_HUB_TABS.map((tab) => tab.id);
    const index = ids.indexOf(activeTab);
    let next: number | null = null;
    if (event.key === "ArrowRight") next = (index + 1) % ids.length;
    if (event.key === "ArrowLeft") next = (index - 1 + ids.length) % ids.length;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = ids.length - 1;
    if (next === null) return;
    event.preventDefault();
    changeTab(ids[next]);
    tabRefs.current[ids[next]]?.focus();
  };

  const startTour = () => {
    onClose();
    replayHomeTour();
  };

  return (
    <FocusTrap
      focusTrapOptions={{
        clickOutsideDeactivates: false,
        escapeDeactivates: false,
        allowOutsideClick: true,
        initialFocus: `#${HEADING_ID}`,
        fallbackFocus: `#${DIALOG_ID}`,
      }}
    >
      <div
        id={DIALOG_ID}
        role="dialog"
        aria-modal="true"
        aria-labelledby={HEADING_ID}
        tabIndex={-1}
        className="flex h-full w-full flex-col overflow-hidden rounded-lg bg-container-neutral-white text-fg-neutral-strong shadow-[0px_2px_6px_0px_rgba(7,7,8,0.12)] outline-none"
      >
        {/* Header bar: the ✕ alone, on the hero's tint. */}
        <div className="flex shrink-0 items-center bg-surface-tertiary px-1 py-2">
          <button
            type="button"
            onClick={onDismiss}
            aria-label={i18n.get("learningHubClose")}
            className="focus-blue flex h-10 w-10 items-center justify-center rounded-lg bg-bg-brand-ghost-default transition-colors hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-active"
          >
            <XIcon size={20} aria-hidden="true" />
          </button>
        </div>

        {/* Hero and tabs: fixed above whichever tab is scrolling. */}
        <div className="flex shrink-0 flex-col items-center gap-2 bg-surface-tertiary px-4">
          <div className="flex w-full flex-col gap-2 text-center">
            <h2
              id={HEADING_ID}
              tabIndex={-1}
              // 32/40 extra-bold: no typography utility at this weight.
              className="text-[32px] font-extrabold leading-10 tracking-[-0.012em] outline-none"
            >
              {`${i18n.get("learningHubHeadingLead")} `}
              <span className="text-fg-brand-subtle">
                {i18n.get("learningHubHeadingBrand")}
              </span>
            </h2>
            <p className="body-m-medium text-fg-neutral-subtle">
              {i18n.get("learningHubSubtitle")}
            </p>
          </div>

          {/* Figma annotation: "Show tooltip guided tour on homepage." */}
          <button
            type="button"
            onClick={startTour}
            className="focus-blue flex min-h-8 items-center gap-1.5 rounded-lg border border-border-token-brand-minimal bg-bg-brand-secondary-default py-1.5 pl-2 pr-1.5 label-m-medium transition-colors hover:bg-bg-brand-secondary-hover"
          >
            {i18n.get("learningHubGuidedTour")}
            <ArrowRightIcon size={20} aria-hidden="true" />
          </button>

          <div
            role="tablist"
            aria-label={i18n.get("learningHubHeadingLead") + " " + i18n.get("learningHubHeadingBrand")}
            className="flex w-full justify-center gap-2 border-b border-border-token-brand-minimal"
          >
            {LEARNING_HUB_TABS.map((tab) => {
              const TabIcon = TAB_ICONS[tab.id];
              const selected = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  ref={(node) => {
                    tabRefs.current[tab.id] = node;
                  }}
                  id={tabId(tab.id)}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-controls={panelId(tab.id)}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => changeTab(tab.id)}
                  onKeyDown={onTabKeyDown}
                  className="focus-blue rounded-md px-2 transition-colors hover:bg-bg-brand-ghost-hover"
                >
                  <span
                    className={cn(
                      "flex items-center gap-2 border-b-2 py-3",
                      selected
                        ? "border-fg-brand-subtle label-m-semibold text-fg-brand-subtle"
                        : "border-transparent label-m-medium text-fg-neutral-subtle",
                    )}
                  >
                    <TabIcon size={20} aria-hidden="true" />
                    {i18n.get(tab.labelKey)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div
          id={panelId(activeTab)}
          role="tabpanel"
          aria-labelledby={tabId(activeTab)}
          className="flex min-h-0 flex-1"
        >
          {activeTab === "find-places" ? <FindPlacesPanel /> : <ContributePanel />}
        </div>
      </div>
    </FocusTrap>
  );
};

/** The "Find places" walkthrough — native's tab, centred in a reading column. */
const FindPlacesPanel = () => {
  const i18n = useI18N();
  const albumName = LEARNING_HUB_TABS.find((tab) => tab.id === "find-places")?.albumName;
  const { steps, isLoading } = useLessonSteps(albumName);

  // The tab is the find_places lesson drawn inline; mounted each time it is
  // shown, so each showing is one opening.
  useLessonOpened(FIND_PLACES_LESSON, (state) =>
    lessonAnalytics.track("page_view_page", {
      lesson_name: lessonName("find-places"),
      entry_point: "find_places",
      lesson_status: lessonStatus(state),
      dont_show_again: false,
    }),
  );

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[600px] p-4">
        <LessonStepList
          steps={steps}
          isLoading={isLoading}
          loadingLabel={i18n.get("learningHubFindPlacesLoading")}
          unavailableLabel={i18n.get("learningHubFindPlacesUnavailable")}
        />
      </div>
    </div>
  );
};

/**
 * "Contribute": the lessons on the left, the picked one on the right. Opens
 * on the first lesson so the right-hand side is never empty.
 */
const ContributePanel = () => {
  const i18n = useI18N();
  const { stateFor, markViewed } = useLearningProgress();
  const [selectedId, setSelectedId] = useState(ALL_LEARNING_ITEMS[0]?.id);
  const lessonHeadingRef = useRef<HTMLHeadingElement>(null);
  const sections =
    LEARNING_HUB_TABS.find((tab) => tab.id === "contribute")?.sections ?? [];
  const selected = ALL_LEARNING_ITEMS.find((item) => item.id === selectedId);

  // One lesson_viewed per lesson shown in the pane, including the first one,
  // which opens pre-selected.
  useLessonOpened(selected, (state) => {
    if (!selected) return;
    lessonAnalytics.track("page_view_page", {
      lesson_name: lessonName(selected.id),
      entry_point: "contribute",
      lesson_status: lessonStatus(state),
      dont_show_again: false,
    });
  });

  // Showing a lesson is reading it — the right-hand pane is native's lesson
  // page, which marks itself viewed on open.
  useEffect(() => {
    if (selectedId) markViewed(selectedId);
  }, [selectedId, markViewed]);

  const selectLesson = (item: LearningItem) => {
    setSelectedId(item.id);
    // Below `md` the lesson stacks under the list, out of sight — take the
    // reader to it. Side by side, focus stays on the row they pressed.
    if (!window.matchMedia("(min-width: 768px)").matches) {
      requestAnimationFrame(() => lessonHeadingRef.current?.focus());
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto md:flex-row md:overflow-hidden">
      <div className="flex shrink-0 flex-col gap-4 p-4 md:w-[375px] md:overflow-y-auto">
        {sections.map((section) => (
          <section key={section.id} aria-labelledby={`${DIALOG_ID}-${section.id}`} className="flex flex-col gap-4">
            <h3 id={`${DIALOG_ID}-${section.id}`} className="body-l-semibold">
              {i18n.get(section.headlineKey)}
            </h3>
            <ul className="flex flex-col gap-2">
              {section.items.map((item) => (
                <li key={item.id}>
                  <LessonRow
                    item={item}
                    state={stateFor(item)}
                    isSelected={item.id === selectedId}
                    onSelect={() => selectLesson(item)}
                  />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <div aria-hidden="true" className="hidden w-px shrink-0 bg-border-token-brand-minimal md:block" />

      {selected ? (
        // Keyed by lesson so the pane starts at the top of each one.
        <LessonPane key={selected.id} item={selected} headingRef={lessonHeadingRef} />
      ) : null}
    </div>
  );
};

/** One lesson row (Figma `Menu/XLarge-56`): its circle, title, subtitle, caret. */
const LessonRow = ({
  item,
  state,
  isSelected,
  onSelect,
}: {
  item: LearningItem;
  state: LearningItemState;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const i18n = useI18N();
  const stateLabel = i18n.get(
    state === "completed"
      ? "learningHubStateCompleted"
      : state === "seen"
        ? "learningHubStateSeen"
        : "learningHubStateNotSeen",
  );

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={isSelected ? "true" : undefined}
      className={cn(
        "focus-blue flex w-full items-start gap-2 rounded-lg border bg-container-neutral-white p-4 text-left transition-colors hover:bg-bg-brand-ghost-hover",
        isSelected ? "border-border-token-brand-subtle" : "border-border-token-brand-minimal",
      )}
    >
      <StateCircle state={state} />
      <span className="flex min-w-0 flex-1 flex-col px-1">
        <span
          className={cn(
            "body-base-medium",
            isSelected ? "text-fg-brand-subtle" : "text-fg-neutral-strong",
          )}
        >
          {i18n.get(item.titleKey)}
        </span>
        <span className="body-s-regular text-fg-neutral-subtle">
          {i18n.get(item.subtitleKey)}
        </span>
        <span className="sr-only">{`, ${stateLabel}`}</span>
      </span>
      <CaretRightIcon size={20} aria-hidden="true" className="ml-auto shrink-0 text-fg-neutral-strong" />
    </button>
  );
};

/**
 * Native's three circles: grey outline (not opened), brand duotone (opened),
 * filled brand check (the lesson's action is done).
 */
const StateCircle = ({ state }: { state: LearningItemState }) => {
  if (state === "completed") {
    return <CheckCircleIcon size={20} weight="fill" aria-hidden="true" className="shrink-0 text-fg-brand-subtle" />;
  }
  if (state === "seen") {
    return <CircleIcon size={20} weight="duotone" aria-hidden="true" className="shrink-0 text-fg-brand-subtle" />;
  }
  return <CircleIcon size={20} aria-hidden="true" className="shrink-0 text-fg-neutral-minimal" />;
};

const LessonPane = ({
  item,
  headingRef,
}: {
  item: LearningItem;
  headingRef: RefObject<HTMLHeadingElement>;
}) => {
  const i18n = useI18N();
  const { steps, isLoading } = useLessonSteps(item.albumName);

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-4 p-4 md:overflow-y-auto">
      <h3 ref={headingRef} tabIndex={-1} className="title-m-bold outline-none">
        {i18n.get(item.lessonTitleKey ?? item.titleKey)}
      </h3>
      <LessonStepList
        steps={steps}
        isLoading={isLoading}
        loadingLabel={i18n.get("learningLessonLoading")}
        unavailableLabel={i18n.get("learningLessonUnavailable")}
      />
    </div>
  );
};

export default LearningHubModal;
